import { LoadingOutlined } from '@ant-design/icons';
import { Dropdown, List, Menu, Skeleton, Tooltip } from 'antd';
import Table, { ColumnType } from 'antd/lib/table';
import dayjs from 'dayjs';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntercom } from 'react-use-intercom';
import styled, { css, CSSProperties } from 'styled-components';

import { CopySvg, DeleteSvg, PencilSvg } from '../../assets/icons';
import AnswerSvg from '../../assets/icons/AnswerSvg';
import ChartSvg from '../../assets/icons/ChartSvg';
import CuriOpenArms from '../../assets/icons/CuriOpenArms';
import DownloadSvg from '../../assets/icons/DownloadSvg';
import ExternalLinkSvg from '../../assets/icons/ExternalLinkSvg';
import FoldedHandsSVG from '../../assets/icons/FoldedHands';
import FolderSvg from '../../assets/icons/FolderSvg';
import MoreSvg from '../../assets/icons/MoreSvg';
import Play from '../../assets/icons/Play';
import PremiumIconFilled from '../../assets/icons/PremiumIconFilled';
import PromptsSvg from '../../assets/icons/PromptsSvg';
import QuickPlaySvg from '../../assets/icons/QuickPlaySvg';
import ShareSvg from '../../assets/icons/ShareSvg';
import theme from '../../assets/theme';
import useAutosaveTemplate from '../../hooks/useAutosaveTemplate';
import { useBreakPoint } from '../../hooks/useBreakPoint';
import useFolders from '../../hooks/useFolders';
import { useMetrics } from '../../hooks/useMetrics';
import usePaywallPrompt from '../../hooks/usePaywallPrompt';
import usePresent from '../../hooks/usePresent';
import useRoutePrefetch from '../../hooks/useRoutePrefetch';
import useRouterMatch from '../../hooks/useRouterMatch';
import useTemplates from '../../hooks/useTemplates';
import {
  Folder,
  FolderEditOrCreateDTO,
  Template,
  TemplateEditOrCreateDTO,
} from '../../services/TemplateService/Template.type';
import { exportPdf } from '../../utils/exportPdf';
import { getTitle } from '../../utils/templateUtils';
import { Input } from '../_atoms/_Input';
import { Button } from '../_atoms/Button';
import { IconWrapper } from '../_atoms/IconWrapper';
import BaseModal from '../_molecules/BaseModal';
import LiveEmptyState from '../_molecules/LiveEmptyState';
import ShareLinkComponent from '../_molecules/ShareLinkComponent';
import LessonBreadcrumbs from '../_organisms/LessonBreadcrumbs/LessonBreadcrumbs';
import MoveToFolder from '../_organisms/MoveToFolder/MoveToFolder';
import NewAppLayout from '../layouts/NewAppLayout';
import { Slide } from '../SlideComponents/slide.types';

const LessonsPage: React.FC<LessonsPageProps> = ({}) => {
  const router = useRouter();

  const { t } = useTranslation('Host');
  const metrics = useMetrics();

  useRoutePrefetch(`/[tenantId]/lessons/[templateId]/edit`, undefined, {
    priority: true,
  });
  const [isDownloadPDF, setIsDownloadPDF] = useState<boolean>(false);
  const { presentLesson, isCreatingGameCurimote, isCreatingGamePresent } = usePresent();
  const isDesktop = useBreakPoint(800);
  const [tableData, setTableData] = useState<TableDataType[]>([]);
  const [isEditBlueprintOpen, setIsEditBlueprintOpen] = useState<boolean>(false);
  const [isDeletingData, setIsDeletingData] = useState<Template | Folder>();
  const { workspaceId, urlTenantId: tenantId, parentFolderId } = useRouterMatch();
  const [editBlurprintAction, setEditBlueprintAction] = useState<
    'newFolder' | 'renameBlueprint' | 'share' | null
  >(null);
  const [isMovingData, setIsMovingData] = useState<Template | Folder>();
  const [editBlueprintChanges, setEditBlueprintChanges] = useState<
    TemplateEditOrCreateDTO | FolderEditOrCreateDTO | null
  >(null);
  const [selectedBlueprint, setSelectedBlueprint] = useState<TableDataType | null>(null);

  const {
    createFolderWithConfig,
    currentFolders,
    allFolders,
    isFetchingFolders,
    isDeletingFolder,
    breadcrumbs,
    deleteFolder,
    saveFolder,
    isSavingFolder,
    isCreatingFolder,
  } = useFolders(parentFolderId);

  const { trackEvent } = useIntercom();

  const { autoSaveTemplate, isSavingTemplate } = useAutosaveTemplate();
  const {
    deleteTemplate,
    isDeletingTemplate,
    templates,
    duplicateTemplate,
    isFetchingTemplates,
    isDuplicatingTemplate,
  } = useTemplates(parentFolderId);

  const handlePresent = useCallback(
    (template: Template, method: 'present' | 'curimote') => {
      presentLesson({
        template,
        method,
        fromBeginning: true,
        gameId: template.content.activeGame?.id,
      });
    },
    [presentLesson],
  );

  const handleCreateFolder = () => {
    createFolderWithConfig({
      folderConfig: {
        title: editBlueprintChanges?.title
          ? editBlueprintChanges.title
          : 'Untitled folder',
        parentId: router.query.parentFolderId ? router.query.parentFolderId : null,
      } as FolderEditOrCreateDTO,
      onSuccessFunction: data => {
        handleCloseModal();
        router.push(`/${tenantId}/lessons?parentFolderId=${data.id}`);
      },
    });
  };

  const handleCreateTemplate = () => {
    trackEvent('PresentationCreated');
    metrics.logEvent('Lessons.NewTemplate.Start', {
      placement: 'lessonTable',
      path: router.pathname,
      workspaceId: workspaceId,
    });
  };
  const handleEditBlueprint = () => {
    if (selectedBlueprint) {
      if (selectedBlueprint.type === 'template') {
        autoSaveTemplate({
          templateConfig: {
            ...editBlueprintChanges,
            title: !!editBlueprintChanges?.title ? editBlueprintChanges?.title : null,
            modifiedAt: new Date().getTime(),
            id: selectedBlueprint.id,
          } as TemplateEditOrCreateDTO,
          onSuccessFunction: () => {
            handleCloseModal();
          },
        });
      }
      if (selectedBlueprint.type === 'folder') {
        saveFolder({
          folderConfig: {
            ...editBlueprintChanges,
            id: selectedBlueprint.id,
          } as FolderEditOrCreateDTO,
          onSuccessFunction: () => {
            handleCloseModal();
          },
        });
      }
    }
  };

  const {
    isReady,
    promptPaywall,
    paywallComponent,
    isEnterprise,
    isPremium,
  } = usePaywallPrompt({
    startState: { screen: 'chooseProduct' },
  });

  const handleExportAsPdf = (template: Template) => {
    const allowedSlideTypes = [
      'wordcloud',
      'slide',
      'answers',
      'aiAssessedAnswers',
      'poll',
      'canvas',
    ];
    const slides = template?.content.slides ?? [];
    const slidesToBeExported = slides.filter(slide =>
      allowedSlideTypes.includes(slide.type),
    );
    exportPdf(
      slidesToBeExported,
      `${getTitle({
        title: template.title,
        slides: template.content.slides,
        maxLength: 30,
      })}`,
      () => setIsDownloadPDF(false),
    );
  };

  const handleDownloadPDFClicked = useCallback(
    (template: Template) => {
      if (!isReady) return;
      if (!isEnterprise && !isPremium) {
        metrics.logEvent('Upgrade.Clicked', { location: 'Lessons.Download.PDF' });
        promptPaywall();
      } else {
        setIsDownloadPDF(true);
        metrics.logEvent('DownloadPDF', { from: 'Lessons.Download.PDF' });
        handleExportAsPdf(template);
      }
    },
    [isEnterprise, isPremium, isReady, metrics, promptPaywall],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleRowClick = (e: any, records: TableDataType) => {
    setSelectedBlueprint(records);
    if (e && records.type === 'folder') {
      router.push({
        pathname: `/${tenantId}/lessons`,
        query: { parentFolderId: records.id },
      });
    }
    if (e && records.type === 'template') {
      router.push({
        pathname: `/${tenantId}/lessons/${records.id}/edit`,
      });
    }
  };

  const handleCloseModal = () => {
    setIsEditBlueprintOpen(false);
    setEditBlueprintAction(null);
    setEditBlueprintChanges(null);
    setSelectedBlueprint(null);
  };
  // Create the table data
  useMemo(() => {
    const fetchedTemplates = templates || [];
    const fetchedFolders = currentFolders || [];
    const tableData = fetchedFolders
      ?.concat(fetchedTemplates)
      .map((record: Template | Folder) => {
        const row: TableDataType = {
          template: record.type === 'template' ? (record as Template) : undefined,
          id: record.id,
          key: record.id,
          title:
            record.type === 'template'
              ? getTitle({
                  title: record.title,
                  slides:
                    record.type === 'template'
                      ? (record as Template).content.slides
                      : null,
                  maxLength: 75,
                })
              : record.title,
          type: record.type,
          slides:
            record.type === 'template' && !!(record as Template).content.slides
              ? (record as Template).content.slides
              : null,
          timesPlayed:
            record.type === 'folder' ? -1 : (record as Template).content.gameIds.length,
          modifiedAt: record.modifiedAt,
          createdAt: dayjs(record.createdAt).fromNow(),
          createdBy: 'Me',
          actions: (
            <Menu>
              {record.type === 'folder' && (
                <>
                  <Menu.Item key={'rename'} style={{ padding: 0 }}>
                    <DropdownMenuItemButton
                      onClick={() => {
                        metrics.logEvent('Lessons.RenameFolder.Start');
                        setEditBlueprintAction('renameBlueprint');
                        setIsEditBlueprintOpen(true);
                      }}
                    >
                      <PencilSvg /> {t('Rename')}
                    </DropdownMenuItemButton>
                  </Menu.Item>
                  <Menu.Item key={'move'} style={{ padding: 0 }}>
                    <DropdownMenuItemButton
                      onClick={() => {
                        metrics.logEvent('Lessons.MoveFolder.Start');
                        setIsMovingData(record);
                      }}
                    >
                      <FolderSvg /> {t('Move')}
                    </DropdownMenuItemButton>
                  </Menu.Item>
                  <div
                    key="divider-folder"
                    style={{ width: '100%', height: 1, backgroundColor: '#C4C4C4' }}
                  />
                  <Menu.Item key={'delete'} style={{ padding: 0 }}>
                    <DropdownMenuItemButton
                      style={{ color: theme.colors.red }}
                      onClick={() => {
                        metrics.logEvent('Lessons.DeleteFolder.Start');
                        setIsDeletingData(record);
                      }}
                    >
                      <DeleteSvg /> {t('Delete')}
                    </DropdownMenuItemButton>
                  </Menu.Item>
                </>
              )}
              {record.type === 'template' && (
                <>
                  <Menu.Item key={'present'} style={{ padding: 0 }}>
                    <DropdownMenuItemButton
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        metrics.logEvent('Lessons.Template.Present');
                        handlePresent(record as Template, 'present');
                      }}
                      disabled={
                        !(record as Template).content.slides ||
                        (record as Template).content.slides?.length === 0
                      }
                    >
                      {isCreatingGamePresent ? <LoadingOutlined /> : <QuickPlaySvg />}{' '}
                      {(record as Template).content.activeGame?.isOngoing
                        ? t('Resume')
                        : t('Present')}
                    </DropdownMenuItemButton>
                  </Menu.Item>
                  <Menu.Item key={'curimote'} style={{ padding: 0 }}>
                    <DropdownMenuItemButton
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        metrics.logEvent('Lessons.Template.OpenCurimote');
                        handlePresent(record as Template, 'curimote');
                      }}
                      disabled={
                        !(record as Template).content.slides ||
                        (record as Template).content.slides?.length === 0
                      }
                    >
                      {isCreatingGameCurimote ? <LoadingOutlined /> : <ExternalLinkSvg />}{' '}
                      {(record as Template).content.activeGame?.isOngoing
                        ? t('Resume with moderation')
                        : t('Present with moderation')}
                    </DropdownMenuItemButton>
                  </Menu.Item>
                  <div
                    key="divider-template-1"
                    style={{ width: '100%', height: 1, backgroundColor: '#C4C4C4' }}
                  />
                  <Menu.Item key={'share'} style={{ padding: 0 }}>
                    <DropdownMenuItemButton
                      onClick={() => {
                        metrics.logEvent('Lessons.Template.Share');
                        setEditBlueprintAction('share');
                        setIsEditBlueprintOpen(true);
                      }}
                    >
                      <ShareSvg /> {t('Share')}
                    </DropdownMenuItemButton>
                  </Menu.Item>
                  <Menu.Item key={'results'} style={{ padding: 0 }}>
                    <DropdownMenuItemButton
                      onClick={e => {
                        e.stopPropagation();
                        router.push(`/${tenantId}/reports/?templateId=${record.id}`);
                      }}
                    >
                      <ChartSvg /> {t('See reports')}
                    </DropdownMenuItemButton>
                  </Menu.Item>
                  <Menu.Item key={'download'} style={{ padding: 0 }}>
                    <DropdownMenuItemButton
                      onClick={() => handleDownloadPDFClicked(record as Template)}
                      disabled={isDownloadPDF}
                    >
                      {isDownloadPDF ? <LoadingOutlined /> : <DownloadSvg />}{' '}
                      {t('Download PDF')}
                      <IconWrapper
                        style={{
                          marginLeft: 'auto',
                          color:
                            isPremium || isEnterprise
                              ? theme.colors.black
                              : theme.colors.yellow,
                        }}
                      >
                        <PremiumIconFilled />
                      </IconWrapper>
                    </DropdownMenuItemButton>
                  </Menu.Item>
                  <div
                    key="divider2"
                    style={{ width: '100%', height: 1, backgroundColor: '#C4C4C4' }}
                  />
                  <Menu.Item key={'duplicate'} style={{ padding: 0 }}>
                    <DropdownMenuItemButton
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        metrics.logEvent('Lessons.Template.Duplicate');
                        if (!isDuplicatingTemplate) {
                          duplicateTemplate({
                            templateId: record.id,
                            parentId: record.parentId,
                            isPrivate: true,
                          });
                        }
                      }}
                    >
                      {isDuplicatingTemplate ? <LoadingOutlined /> : <CopySvg />}{' '}
                      {t('Duplicate')}
                    </DropdownMenuItemButton>
                  </Menu.Item>
                  <Menu.Item key={'rename'} style={{ padding: 0 }}>
                    <DropdownMenuItemButton
                      onClick={() => {
                        metrics.logEvent('Lessons.RenameTemplate.Start');
                        setEditBlueprintAction('renameBlueprint');
                        setIsEditBlueprintOpen(true);
                      }}
                    >
                      <PencilSvg /> {t('Rename')}
                    </DropdownMenuItemButton>
                  </Menu.Item>
                  <Menu.Item key={'edit'} style={{ padding: 0 }}>
                    <Link href={`/${tenantId}/lessons/${record.id}/edit`}>
                      <a>
                        <DropdownMenuItemButton
                          onClick={() => {
                            metrics.logEvent('Lessons.Template.Edit');
                          }}
                        >
                          <AnswerSvg /> {t('Edit')}
                        </DropdownMenuItemButton>
                      </a>
                    </Link>
                  </Menu.Item>
                  <Menu.Item key={'move'} style={{ padding: 0 }}>
                    <DropdownMenuItemButton
                      onClick={() => {
                        metrics.logEvent('Lessons.Template.Move');
                        setIsMovingData(record);
                      }}
                    >
                      <FolderSvg /> {t('Move')}
                    </DropdownMenuItemButton>
                  </Menu.Item>
                  <div
                    key="divider-template2"
                    style={{ width: '100%', height: 1, backgroundColor: '#C4C4C4' }}
                  />
                  <Menu.Item key={'delete'} style={{ padding: 0 }}>
                    <DropdownMenuItemButton
                      style={{ color: theme.colors.red }}
                      onClick={() => {
                        metrics.logEvent('Lessons.Template.Delete');
                        setIsDeletingData(record);
                      }}
                    >
                      <DeleteSvg /> {t('Delete')}
                    </DropdownMenuItemButton>
                  </Menu.Item>
                </>
              )}
            </Menu>
          ),
        };
        return row;
      });
    setTableData(tableData);
  }, [
    templates,
    currentFolders,
    t,
    isCreatingGamePresent,
    isCreatingGameCurimote,
    isDownloadPDF,
    isPremium,
    isEnterprise,
    isDuplicatingTemplate,
    tenantId,
    metrics,
    handlePresent,
    router,
    handleDownloadPDFClicked,
    duplicateTemplate,
  ]);
  const hasNoBlueprints = templates && templates.length === 0;

  const columns: ColumnType<TableDataType>[] = isDesktop
    ? [
        {
          title: t('Name'),
          dataIndex: ['title', 'type'],
          key: 'title',
          align: 'left',
          sorter: (a, b, sortOrder) => {
            // folders always on the top. Negative when sorting descending
            const descendingMultiplier = sortOrder === 'descend' ? -1 : 1;
            if (a.type === 'folder' && b.type !== 'folder')
              return 0 * descendingMultiplier;
            if (a.type !== 'folder' && b.type === 'folder')
              return 1 * descendingMultiplier;
            const aTitle = getTitle({ title: a.title, slides: a.slides || [] });
            const bTitle = getTitle({ title: b.title, slides: a.slides || [] });
            if (aTitle < bTitle) {
              return -1;
            }
            if (aTitle > bTitle) {
              return 1;
            }
            return 0;
          },
          render: (text: string, record: TableDataType) => {
            return (
              <BlueprintColumn>
                {record.type === 'folder' && (
                  <IconWrapper height="25px" width="25px" style={{ flex: '0 1 25px' }}>
                    <FolderSvg />
                  </IconWrapper>
                )}
                {record.type === 'template' &&
                  !(
                    record.template?.aiContent === undefined ||
                    record.template?.aiContent === null
                  ) && (
                    <Tooltip title="This lesson is a generator" mouseEnterDelay={1}>
                      <IconWrapper
                        height="22px"
                        color={theme.colors.black}
                        width="22px"
                        style={{ flex: '0 1 25px' }}
                      >
                        <PromptsSvg />
                      </IconWrapper>
                    </Tooltip>
                  )}
                {record.type === 'template' &&
                  (record.template?.aiContent === undefined ||
                    record.template?.aiContent === null) && (
                    <IconWrapper
                      height="22px"
                      width="22px"
                      onClick={e => {
                        e.stopPropagation();
                        if (record.template) {
                          metrics.logEvent('Lessons.Template.Present.PlayButton');
                          setSelectedBlueprint(record);
                          if (record.template.content.activeGame?.isOngoing) {
                            router.push(
                              `/${tenantId}/lessons/${record.template.id}/edit`,
                            );
                          } else {
                            handlePresent(record.template, 'present');
                          }
                        }
                      }}
                    >
                      {selectedBlueprint &&
                      selectedBlueprint.id === record.id &&
                      isCreatingGamePresent ? (
                        <LoadingOutlined />
                      ) : (
                        <IconWrapper>
                          <Play height={22} width={22} />
                        </IconWrapper>
                      )}
                    </IconWrapper>
                  )}
                <div style={{ marginLeft: '35px' }}>{record.title}</div>
              </BlueprintColumn>
            );
          },
        },
        {
          title: t('Modified'),
          dataIndex: 'modifiedAt',
          align: 'left',
          key: 'modifiedAt',
          width: 170,
          sorter: (a, b, sortOrder) => {
            // folders always on the top. Negative when sorting descending
            const descendingMultiplier = sortOrder === 'descend' ? -1 : 1;
            if (a.type === 'folder' && b.type !== 'folder')
              return 0 * descendingMultiplier;
            if (a.type !== 'folder' && b.type === 'folder')
              return 1 * descendingMultiplier;

            if (a.modifiedAt < b.modifiedAt) {
              return 1;
            }
            if (a.modifiedAt > b.modifiedAt) {
              return -1;
            }

            return 0;
          },
          render: (modifiedAt: number, record: TableDataType) => {
            const diff = dayjs().diff(dayjs(modifiedAt), 'hours');
            return (
              <BlueprintColumn>
                <Link
                  href={
                    record.type === 'folder'
                      ? `/${tenantId}/lessons?parentFolderId=${record.id}`
                      : `/${tenantId}/lessons/${record.id}`
                  }
                >
                  <a>
                    {diff >= 24
                      ? dayjs(modifiedAt)
                          .format('HH:mm, MMM D, YYYY')
                          .toString()
                      : `${dayjs(modifiedAt).fromNow()} `}
                  </a>
                </Link>
              </BlueprintColumn>
            );
          },
        },
        {
          title: t('Times played'),
          dataIndex: 'timesPlayed',
          key: 'timesPlayed',
          align: 'left',
          width: 150,
          sorter: (a, b, sortOrder) => {
            // folders always on the top. Negative when sorting descending
            const descendingMultiplier = sortOrder === 'descend' ? -1 : 1;
            if (a.type === 'folder' && b.type !== 'folder')
              return 0 * descendingMultiplier;
            if (a.type !== 'folder' && b.type === 'folder')
              return 1 * descendingMultiplier;

            if (a.timesPlayed < b.timesPlayed) {
              return 1;
            }
            if (a.timesPlayed > b.timesPlayed) {
              return -1;
            }

            return 0;
          },
          render: (timesPlayed: number) => (
            <BlueprintColumn $align={'right'}>
              {timesPlayed === -1 ? '-' : timesPlayed}
            </BlueprintColumn>
          ),
        },
        {
          title: t('Options'),
          dataIndex: 'actions',
          width: '50px',
          key: 'actions',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          render: (text: any, record: TableDataType) => (
            <div
              onClick={e => {
                metrics.logEvent('Lessons.TableRow.Options.Open');
                setSelectedBlueprint(record);
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Dropdown overlay={text} trigger={['click']} placement="bottomRight">
                <IconWrapper color="#A5A5A5" height="24px" width="24px">
                  <MoreSvg />
                </IconWrapper>
              </Dropdown>
            </div>
          ),
        },
      ]
    : [
        {
          title: t('Name'),
          dataIndex: ['title', 'type'],
          key: 'title',
          align: 'left',
          render: (text: string, record: TableDataType) => {
            return (
              <BlueprintColumn>
                {record.type === 'folder' ? (
                  <IconWrapper height="25px" width="25px" style={{ flex: '0 1 25px' }}>
                    <FolderSvg />
                  </IconWrapper>
                ) : (
                  <IconWrapper
                    height="22px"
                    width="22px"
                    onClick={e => {
                      e.stopPropagation();
                      if (record.template) {
                        metrics.logEvent('Lessons.Template.Present.PlayButton');
                        setSelectedBlueprint(record);
                        if (record.template.content.activeGame?.isOngoing) {
                          router.push(`/${tenantId}/lessons/${record.template.id}/edit`);
                        } else {
                          handlePresent(record.template, 'present');
                        }
                      }
                    }}
                  >
                    {selectedBlueprint &&
                    selectedBlueprint.id === record.id &&
                    isCreatingGamePresent ? (
                      <LoadingOutlined />
                    ) : (
                      <Play height={22} width={22} />
                    )}
                  </IconWrapper>
                )}
                <div style={{ marginLeft: '35px' }}>{record.title}</div>
              </BlueprintColumn>
            );
          },
        },
        {
          title: t('Options'),
          dataIndex: 'actions',
          width: '50px',
          key: 'actions',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          render: (text: any, record: TableDataType) => (
            <div
              onClick={e => {
                metrics.logEvent('Lessons.TableRow.Options.Open');
                setSelectedBlueprint(record);
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Dropdown overlay={text} trigger={['click']}>
                <IconWrapper color="#A5A5A5" height="24px" width="24px">
                  <MoreSvg />
                </IconWrapper>
              </Dropdown>
            </div>
          ),
        },
      ];

  const handleDelete = (record: Template | Folder) => {
    if (record.hasOwnProperty('content')) {
      deleteTemplate(record as Template);
    } else {
      deleteFolder(record as Folder);
    }
    setIsDeletingData(undefined);
  };

  const createLessonLink: string | undefined = useMemo(() => {
    if (parentFolderId && workspaceId)
      return `/${tenantId}/lessons/new/edit?workspaceId=${workspaceId}&parentFolderId=${parentFolderId}`;
    if (workspaceId) return `/${tenantId}/lessons/new/edit?workspaceId=${workspaceId}`;
    if (parentFolderId)
      return `/${tenantId}/lessons/new/edit?parentFolderId=${parentFolderId}`;
    return `/${tenantId}/lessons/new/edit`;
  }, [parentFolderId, tenantId, workspaceId]);

  return (
    <NewAppLayout>
      <Head>
        <title>{t('My lessons')}</title>
      </Head>
      {paywallComponent}
      <LiveContainer>
        <ContentContainer $isDesktop={isDesktop}>
          <LessonContainer>
            <LessonPageHeaderContainer>
              {!!!tableData.length &&
                !isFetchingTemplates &&
                !isFetchingFolders &&
                parentFolderId === undefined && (
                  <EmptyLessonsPage>
                    <CuriOpenArms />
                    <Title $fontSize={25}>
                      You have no lessons yet, create your first engaging lesson
                    </Title>
                    {createLessonLink && (
                      <Link href={createLessonLink}>
                        <a>
                          <Button
                            styles={{
                              paddingLeft: 16,
                              paddingRight: 16,
                              alignSelf: 'center',
                            }}
                            height={isDesktop ? '38px' : '42px'}
                            width={isDesktop ? 'auto' : '100%'}
                            onClick={handleCreateTemplate}
                          >
                            + {t('Create lesson')}
                          </Button>
                        </a>
                      </Link>
                    )}
                  </EmptyLessonsPage>
                )}
              {breadcrumbs && isDesktop && (!!tableData.length || !!parentFolderId) && (
                <LessonBreadcrumbs breadcrumbs={breadcrumbs} />
              )}
              {currentFolders && (!!tableData.length || !!parentFolderId) && (
                <HorizontalDiv $isDesktop={isDesktop}>
                  {isDesktop && (
                    <Button
                      height={isDesktop ? '38px' : '42px'}
                      width={isDesktop ? 'auto' : '100%'}
                      styles={{ paddingLeft: 16, paddingRight: 16 }}
                      type="secondary"
                      onClick={() => {
                        metrics.logEvent('Lessons.NewFolder.Start');
                        setEditBlueprintAction('newFolder');
                        setIsEditBlueprintOpen(true);
                      }}
                      loading={isCreatingFolder}
                    >
                      + {t('New folder')}
                    </Button>
                  )}
                </HorizontalDiv>
              )}
            </LessonPageHeaderContainer>
            {tableData.length > 0 && (
              <TableContainer style={{ marginBottom: !isDesktop ? '70px' : '' }}>
                <Table
                  locale={{
                    triggerAsc: t('ascending', 'Click to sort ascending'),
                    triggerDesc: t('descending', 'Click to sort descending'),
                    cancelSort: t('cancelsort', 'Cancel sorting'),
                  }}
                  rowKey="id"
                  dataSource={tableData}
                  columns={columns}
                  pagination={isFetchingTemplates && !templates ? false : undefined}
                  onRow={record => {
                    return {
                      onClick: e => {
                        handleRowClick(e, record);
                      }, // click row
                    };
                  }}
                />
              </TableContainer>
            )}
            {isFetchingFolders && tableData.length === 0 && !allFolders && (
              <LoadingTable id="folder" rows={3} />
            )}
            {isFetchingTemplates && !templates && (
              <LoadingTable id="template" hideHeader={true} rows={2} />
            )}
            {!isFetchingFolders &&
              hasNoBlueprints &&
              parentFolderId !== undefined &&
              tableData.length === 0 && (
                <LiveEmptyState
                  icon={<FoldedHandsSVG />}
                  title={t('No lessons')}
                  description={t(
                    'To run an open ended, brainstorm, Q&A or drawing activity, hit + Create lesson!',
                  )}
                />
              )}
          </LessonContainer>
        </ContentContainer>
      </LiveContainer>

      {/* ------------ DELETE FOLDER/TEMPLATE MODAL ----------------- */}
      {isDeletingData && (
        <DeleteFolderModal
          handleDelete={handleDelete}
          deleteData={isDeletingData}
          isDeletingData={isDeletingFolder || isDeletingTemplate}
          folders={
            allFolders ? allFolders.filter(f => f.parentId === isDeletingData.id) : []
          }
          setDeleteData={setIsDeletingData}
        />
      )}
      {!!isMovingData && allFolders && (
        <MoveToFolder
          onCancel={() => setIsMovingData(undefined)}
          data={isMovingData}
          onDone={() => setIsMovingData(undefined)}
        />
      )}
      {editBlurprintAction === 'newFolder' && (
        <BaseModal title={t(`New folder`)} visible onCancel={handleCloseModal}>
          <Input
            placeholder={t('Name the folder')}
            value={editBlueprintChanges?.title ? editBlueprintChanges?.title : ''}
            disabled={isCreatingFolder}
            autoFocus
            maxLength={40}
            onChange={(
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => setEditBlueprintChanges({ title: event.target.value })}
            onPressEnter={() => handleCreateFolder()}
          ></Input>
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '15px',
            }}
          >
            <Button
              type="tertiary"
              styles={{
                border: 'none',
                color: theme.colors.black,
                boxShadow: 'none',
                marginRight: '5px',
              }}
              onClick={() => {
                handleCloseModal();
              }}
            >
              {t('Cancel')}
            </Button>
            <Button
              disabled={isCreatingFolder || !editBlueprintChanges?.title}
              onClick={() => handleCreateFolder()}
            >
              {isCreatingFolder ? (
                <>
                  <LoadingOutlined /> {t('Creating folder')}
                </>
              ) : (
                t('Create folder')
              )}
            </Button>
          </div>
        </BaseModal>
      )}
      {/* ------------ RENAME BLUEPRINT MODAL ----------------- */}
      {editBlurprintAction === 'renameBlueprint' && (
        <BaseModal
          title={`${t('Rename folder', 'Rename')} ${
            selectedBlueprint?.title ? `"${selectedBlueprint.title}"` : ''
          }`}
          visible
          onCancel={handleCloseModal}
        >
          <Input
            placeholder={t('New name')}
            autoFocus
            maxLength={40}
            value={
              editBlueprintChanges === null
                ? selectedBlueprint?.title || ''
                : editBlueprintChanges.title || ''
            }
            disabled={isSavingFolder || isSavingTemplate}
            onChange={(
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) =>
              setEditBlueprintChanges({
                title: event.target.value ? event.target.value : null,
              })
            }
            onPressEnter={() => handleEditBlueprint()}
          ></Input>
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '15px',
            }}
          >
            <Button
              type="tertiary"
              styles={{ border: 'none', color: theme.colors.black, boxShadow: 'none' }}
              onClick={() => {
                handleCloseModal();
              }}
            >
              {t('Cancel')}
            </Button>
            <Button
              disabled={isSavingFolder || isSavingTemplate}
              onClick={() => handleEditBlueprint()}
            >
              {isSavingFolder || isSavingTemplate ? (
                <>
                  <LoadingOutlined /> {t('Renaming')}
                </>
              ) : (
                t('Rename')
              )}
            </Button>
          </div>
        </BaseModal>
      )}
      {editBlurprintAction === 'share' && selectedBlueprint && (
        <BaseModal
          title={t('shareTemplate', {
            defaultValue: 'Share "{{title}}"',
            title: getTitle({
              title: selectedBlueprint.title,
              slides: selectedBlueprint.slides,
              maxLength: 75,
            }),
          })}
          visible={isEditBlueprintOpen}
          onCancel={handleCloseModal}
        >
          <ShareLinkComponent
            templateId={selectedBlueprint.id}
            isGeneratedByAI={!!selectedBlueprint.template?.metadata?.['aiTopic']}
          />
        </BaseModal>
      )}
    </NewAppLayout>
  );
};

const DeleteFolderModal = ({
  deleteData,
  setDeleteData,
  handleDelete,
  isDeletingData,
  folders,
}: {
  deleteData: Template | Folder;
  setDeleteData: (data: Template | Folder | undefined) => void;
  isDeletingData: boolean;
  handleDelete: (data: Template | Folder) => void;
  folders: Folder[];
}) => {
  const { t } = useTranslation('Host');
  const [deletedChildren, setDeletedChildren] = useState<
    (Pick<Template, 'type' | 'title'> | Pick<Folder, 'title' | 'type'>)[] | undefined
  >();
  const templates = useTemplates(deleteData.id).templates;
  useMemo(() => {
    if (deleteData.type === 'folder') {
      const toBeDeletedChildren = folders
        .slice(0, 10)
        .map(f => ({ title: f.title, type: f.type }))
        .concat(
          templates
            ? templates.slice(0, 10).map(t => ({
                title: getTitle({ title: t.title, maxLength: 75 }),
                type: t.type,
              }))
            : [],
        );
      setDeletedChildren(toBeDeletedChildren);
    }
  }, [deleteData, templates, folders]);
  return (
    <>
      <BaseModal
        title={`${t('Delete')}  ${deleteData.title ? `"${deleteData.title}"` : ''}?`}
        visible
        onCancel={() => setDeleteData(undefined)}
      >
        <p
          style={{
            marginTop: 20,
            marginBottom: 20,
            width: '100%',
            textAlign: 'left',
            fontWeight: 'bold',
          }}
        >
          {deletedChildren && (
            <>
              {t('DeleteFolderTexst', {
                defaultValue: `This will delete "{{title}}" along with`,
                title: deleteData.title,
                count: deletedChildren ? deletedChildren.length : 0,
              })}

              <List
                dataSource={deletedChildren}
                renderItem={(
                  item: Pick<Template, 'title' | 'type'> | Pick<Folder, 'title' | 'type'>,
                ) => (
                  <List.Item
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '10px',
                      border: 'none',
                      padding: '10px',
                      marginTop: '5px',
                    }}
                  >
                    <span style={{ fontWeight: 'bold', display: 'flex' }}>
                      <IconWrapper style={{ marginRight: '10px' }}>
                        {item.type === 'folder' && <FolderSvg height={22} width={22} />}
                        {item.type === 'template' && <Play height={22} width={22} />}
                      </IconWrapper>
                      {item.title}
                    </span>
                  </List.Item>
                )}
              />
            </>
          )}
        </p>
        <p>
          {t(
            'deltedPresentationsAnd',
            ' Deleted lessons and folders will be kept in the trash folder for 30 days so you can restore deleted content',
          )}
        </p>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          <Button
            type="tertiary"
            styles={{ border: 'none', color: theme.colors.black, boxShadow: 'none' }}
            onClick={() => {
              setDeleteData(undefined);
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={() => handleDelete(deleteData)}
            width="100px"
            type="danger"
            styles={{ marginLeft: 10 }}
          >
            {isDeletingData && <LoadingOutlined />} {t('Delete')}
          </Button>
        </div>
      </BaseModal>
    </>
  );
};
const HorizontalDiv = styled.div<{ $isDesktop: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  ${({ $isDesktop }) => ($isDesktop ? '' : 'width: 100%')}
`;
const LessonPageHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0px auto 20px auto;
  align-items: center;

  h1 {
    margin: 0;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
  }
`;

const LessonContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;

  h2 {
    font-size: 1.2rem;
    font-weight: 600;
  }
`;

const BlueprintColumn = styled.div<{ $align?: 'right' | 'left' }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  ${({ $align = 'left' }) =>
    $align === 'left'
      ? css`
          justify-content: flex-start;
        `
      : css`
          justify-content: flex-end;
        `}
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: center;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.greyVeryDark};
  margin-top: 10px;
  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 5}px;
  }
`;

const EmptyLessonsPage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const DropdownMenuItemButton = styled.button`
  background: none;
  border: none;
  padding: 5px 12px;
  cursor: pointer;
  width: 100%;
  text-align: left;
  font-weight: bold;
  &:disabled {
    cursor: not-allowed;
  }
  display: flex;
  align-items: center;
  svg {
    margin-right: 10px;
    margin-left: 5px;
    height: 16px;
    width: 16px;
  }
`;

const TableContainer = styled.div`
  .ant-skeleton-element {
    width: 100%;
  }
  .ant-skeleton-input {
    border-radius: 5px;
  }
  .ant-table-cell {
    padding: 15px;
    height: 60px;
  }
`;

type TableSkeletonDataType = {
  id: string;
  name: string;
  modifiedAt: string;
  actions: string;
};

const LoadingTable = ({
  hideHeader,
  rows,
  id,
}: {
  hideHeader?: boolean | null;
  rows?: number | null;
  id: string;
}) => {
  const { t } = useTranslation('Host');

  const isDesktop = useBreakPoint(1000);
  const tableData: TableSkeletonDataType[] = new Array(rows ? rows : 5)
    .fill(0)
    .map((i, index) => ({
      id: `${id}-${index}`,
      name: '',
      modifiedAt: '',
      timesPlayed: '',
      actions: '',
    }));

  const columns: ColumnType<TableSkeletonDataType>[] = isDesktop
    ? [
        {
          title: t('Name'),
          dataIndex: ['name'],
          key: 'name',
          render: () => (
            <>
              <Skeleton.Input active={true} style={{ width: '100%' }} />
            </>
          ),
        },
        {
          title: t('Modified'),
          dataIndex: 'modifiedAt',
          align: 'left',
          key: 'modifiedAt',
          width: 170,
          render: () => (
            <>
              <Skeleton.Input active={true} />
            </>
          ),
        },
        {
          title: t('Times played'),
          dataIndex: 'timesPlayed',
          key: 'timesPlayed',
          align: 'left',
          width: 150,
          render: () => (
            <>
              <Skeleton.Input active={true} />
            </>
          ),
        },
        {
          title: t('Options'),
          dataIndex: 'actions',
          width: 130,
          key: 'actions',
          render: () => (
            <>
              <Skeleton.Input active={true} />
            </>
          ),
        },
      ]
    : [
        {
          title: t('Name'),
          dataIndex: ['name'],
          key: 'name',
          render: () => (
            <>
              <Skeleton.Input active={true} style={{ width: '100%' }} />
            </>
          ),
        },
        {
          title: t('Options'),
          dataIndex: 'actions',
          width: 130,
          key: 'actions',
          render: () => (
            <>
              <Skeleton.Input active={true} />
            </>
          ),
        },
      ];

  return (
    <TableContainer>
      <Table
        rowKey="id"
        dataSource={tableData}
        columns={columns}
        pagination={false}
        showHeader={hideHeader ? false : true}
      />
    </TableContainer>
  );
};

const LiveContainer = styled.div`
  display: flex;
  background-color: ${theme.colors.background};
  height: inherit;
  width: 100%;
  height: 100%;
  padding-top: 60px;
  align-items: flex-start;
  justify-content: flex-start;
`;

const ContentContainer = styled.div<{
  $isDesktop?: boolean;
}>`
  width: 100%;
  height: 100%;
  padding: 40px 40px 80px 40px;

  @media (max-width: 601px) {
    padding: 40px 20px 80px 20px;
  }

  ${({ $isDesktop }) =>
    $isDesktop &&
    css`
      padding-left: 280px;
    `}
  ${({ $isDesktop }) =>
    !$isDesktop &&
    css`
      padding-left: 0px;
    `}
`;

interface LessonsPageProps {
  style?: CSSProperties;
  topicId?: string | null;
}

export type TableDataType = {
  id: string;
  key: string;
  title?: string | null;
  timesPlayed: number;
  createdBy: string;
  modifiedAt: number;
  slides?: Slide[] | null;
  type: 'template' | 'folder';
  actions: React.ReactNode;
  createdAt: string;
  template?: Template;
};
export default LessonsPage;
